import React, { useEffect, useState } from 'react';
import { OfferSummary } from '../../../../applicant_tracking/SignableOffers/SendSignableOfferModal/BodyWrapper/OfferSummary';
import { CloseableModal } from '../../../../../components/CloseableModal';
import { SignableOffer } from '../../../../../entities/v1/applicant_tracking/SignableOffer';
import { Button } from 'reactstrap';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import styles from './styles.module.scss';
import { SignableOfferService } from '../../../../../services/v1/applicant_tracking/SignableOfferService';
import { IconSpan } from '../../../../../components/IconSpan';
import { SignableOfferDocument } from '../../../../../entities/v1/applicant_tracking/SignableOfferDocument';
import { LoadingButton } from '../../../../../components/LoadingButton';
import { Icon } from '../../../../../components/Icon';

interface PropTypes {
  isOpen: boolean;
  signableOfferId: number;
  onClose: () => void;
}

async function previewDocument(
  id: number,
  setLoading: (flag: boolean) => void,
) {
  setLoading(true);

  try {
    const document = await SignableOfferService.getBundleUrl(id);

    if (document?.url) {
      window.open(document.url, '_blank');
    } else {
      console.error('Could not get the document to preview');
    }
  } catch (e: any) {
    console.error('It was not possible to preview the document', e);
  } finally {
    setLoading(false);
  }
}

function buttonLabel(size: number) {
  if (size <= 1) {
    return 'View Document';
  }

  return `View All Documents (${size})`;
}

function SignableDocuments(props: {
  id: number;
  signableDocuments: SignableOfferDocument[];
}) {
  const [loading, setLoading] = useState(false);

  return (
    <>
      <span className='fw-semibold fs-5 mb-2'>
        The candidate will receive these documents to sign:
      </span>
      <div className='bg-gray rounded-2 py-3h px-3'>
        <div>
          {props.signableDocuments.map((d, index) => (
            <IconSpan
              key={index}
              icon={{ name: 'bi-paperclip', className: 'fs-5' }}
              spanClassName='fs-5'
              spanText={d.fileName}
            />
          ))}
          <LoadingButton
            type='button'
            color='borderless'
            className='mt-2'
            size='sm'
            isLoading={loading}
            disabled={props.signableDocuments.length === 0}
            onClick={() => previewDocument(props.id, setLoading)}
          >
            {buttonLabel(props.signableDocuments.length)}{' '}
            {!loading && (
              <Icon
                name={'bi-box-arrow-up-right'}
                className='ms-2 fs-6 text-info'
              />
            )}
          </LoadingButton>
        </div>
      </div>
    </>
  );
}

function OfferSummaryBody(props: { signableOffer: SignableOffer }) {
  if (props.signableOffer == null) {
    return <LoadingSpinner />;
  }

  return (
    <OfferSummary
      alertMessage={
        props.signableOffer?.status === 'expired' &&
        'This offer has expired and is no longer valid. If applicable, a new offer needs to be sent to the candidate.'
      }
      className={styles['modal-body']}
      emailHtml={
        props.signableOffer.email.bodyText || props.signableOffer.email.html
      }
      emailAttachments={props.signableOffer.email.attachments?.map((v) => {
        return {
          filename: v.filename,
          size: null,
        };
      })}
      isLoading={props.signableOffer == null}
      signatoryName={
        props.signableOffer.signingLinks?.find((s) => s.userId != null)?.user
          ?.name
      }
      signableDocumentsNode={
        <SignableDocuments
          id={props.signableOffer.id}
          signableDocuments={props.signableOffer.signableOfferDocuments}
        />
      }
    />
  );
}

export function SignableOfferSummaryModal(props: PropTypes) {
  const [signableOffer, setSignableOffer] = useState<SignableOffer>(null);

  useEffect(() => {
    (async () => {
      if (props.isOpen && signableOffer == null) {
        setSignableOffer(
          await SignableOfferService.show({
            id: props.signableOfferId,
          }),
        );
      }
    })();
  }, [props.isOpen]);

  return (
    <CloseableModal
      size='xl'
      isOpen={props.isOpen}
      className='modal-dialog-centered'
      onClose={props.onClose}
      headerTitle={'Offer Summary'}
      bodyChildren={<OfferSummaryBody signableOffer={signableOffer} />}
      footerChildren={
        <Button color='primary' onClick={props.onClose}>
          Close
        </Button>
      }
    />
  );
}
